/**
 * P3-10 · Specifications panel — the product attribute table. When any
 * attribute carries a `group`, rows render under their group heading (in first-
 * seen order, ungrouped rows last under no heading); otherwise it's one flat
 * key/value list. Values are display-ready off the projection (unit appended).
 */
import type { PdpAttribute } from "~/server/api/schemas/pdp";

/** Attribute value → label; booleans become Yes/No, unit is appended. */
function formatAttributeValue(a: PdpAttribute): string {
  const value =
    typeof a.value === "boolean" ? (a.value ? "Yes" : "No") : String(a.value);
  return a.unit ? `${value} ${a.unit}` : value;
}

function AttributeRows({ rows }: { rows: PdpAttribute[] }) {
  return (
    <dl className="max-w-lg divide-y divide-border text-sm">
      {rows.map((a) => (
        <div key={a.definitionId} className="flex justify-between gap-4 py-2">
          <dt className="text-fg-muted">{a.name}</dt>
          <dd className="text-right text-fg">{formatAttributeValue(a)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SpecsTable({ attributes }: { attributes: PdpAttribute[] }) {
  const hasGroups = attributes.some((a) => a.group);
  if (!hasGroups) return <AttributeRows rows={attributes} />;

  // Preserve first-seen group order; ungrouped rows collapse under a trailing
  // headingless section so nothing is dropped.
  const order: (string | null)[] = [];
  const byGroup = new Map<string | null, PdpAttribute[]>();
  for (const a of attributes) {
    const key = a.group ?? null;
    if (!byGroup.has(key)) {
      byGroup.set(key, []);
      order.push(key);
    }
    byGroup.get(key)!.push(a);
  }
  // Keep named groups first, the ungrouped bucket last.
  order.sort((x, y) => (x === null ? 1 : 0) - (y === null ? 1 : 0));

  return (
    <div className="flex flex-col gap-6">
      {order.map((group) => (
        <section key={group ?? "__ungrouped"}>
          {group && (
            <h3 className="mb-2 text-sm font-semibold text-fg">{group}</h3>
          )}
          <AttributeRows rows={byGroup.get(group)!} />
        </section>
      ))}
    </div>
  );
}
